import { SEED_OFFERINGS } from './offeringDummyData';
import { SEARCHABLE_FIELDS, deriveOfferingStatus } from './offeringConstants';

const clone = obj => JSON.parse(JSON.stringify(obj));

let records = clone(SEED_OFFERINGS);

/** Injects derived `offeringStatus` into a single record without mutating the store. */
export const deriveStatus = (record, now = new Date()) => ({
  ...record,
  offeringStatus: deriveOfferingStatus(record.submissionDeadline, now),
});

/**
 * @param {{ search?: string, page?: number, pageSize?: number, sortField?: string, sortDir?: string, now?: Date }} opts
 * @returns {{ items: object[], totalCount: number }}
 */
export const list = ({
  search = '',
  page = 1,
  pageSize = 10,
  sortField = null,
  sortDir = 'asc',
  now = new Date(),
} = {}) => {
  const needle = search.trim().toLowerCase();

  let result = records
    .map(r => deriveStatus(r, now))
    .filter(r => {
      if (!needle) return true;
      return SEARCHABLE_FIELDS.some(f =>
        String(r[f] ?? '')
          .toLowerCase()
          .includes(needle)
      );
    });

  if (sortField) {
    result = [...result].sort((a, b) => {
      const va = String(a[sortField] ?? '');
      const vb = String(b[sortField] ?? '');
      const cmp = va.localeCompare(vb, undefined, {
        numeric: true,
        sensitivity: 'base',
      });
      return sortDir === 'desc' ? -cmp : cmp;
    });
  }

  const totalCount = result.length;
  const items = result.slice((page - 1) * pageSize, page * pageSize);

  return { items, totalCount };
};

/**
 * @param {string} id
 * @param {{ now?: Date }} opts
 * @returns {object|null}
 */
export const getById = (id, { now = new Date() } = {}) => {
  const found = records.find(r => r.id === id);
  if (!found) return null;
  return deriveStatus(clone(found), now);
};

/**
 * Persists counter state payload onto a record without changing submissionStatus.
 * Used by "Save as Draft".
 */
export const saveDraft = (id, payload, { now = new Date() } = {}) => {
  const record = records.find(r => r.id === id);
  if (!record) throw new Error('Offering not found');
  Object.assign(record, payload);
  record.submissionStatus = 'Draft';
  return deriveStatus(clone(record), now);
};

/**
 * Submits the offer — sets submissionStatus to 'Submitted'.
 * Guards against Closing deadline and double-submit.
 */
export const submitOffer = (id, payload, { now = new Date() } = {}) => {
  const record = records.find(r => r.id === id);
  if (!record) throw new Error('Offering not found');
  if (deriveOfferingStatus(record.submissionDeadline, now) === 'Closing')
    throw new Error('Masa penawaran telah berakhir');
  if (record.submissionStatus === 'Submitted')
    throw new Error('Penawaran sudah diajukan');
  Object.assign(record, payload);
  record.submissionStatus = 'Submitted';
  return deriveStatus(clone(record), now);
};

/** Test utility — restores seed data. */
export const _reset = () => {
  records = clone(SEED_OFFERINGS);
};
